import { getResult } from "@/helpers/gameHelpers";
import {
  Affiliation,
  LiveState,
  Match,
  MatchState,
  MultiScoreboard,
  Result,
  Score,
  TimeState,
} from "@/sources/kicker-amateur/types";
import { MatchOutcome, MatchResult } from "../types";
import { currentLeagueSeasonStart } from "./league";

const REGULAR_LEAGUE = "League";

export function visibleInSchedule(game: Match) {
  const league = game["Contest"];

  return (
    league &&
    game.Kickoff &&
    (league.ContestType === REGULAR_LEAGUE ||
      new Date(game.Kickoff) >= currentLeagueSeasonStart())
  );
}

export function getOpponentTeamByIdFromMatch(match: Match, teamId: number) {
  return `${match.ATeam?.Id}` === `${teamId}` ? match.BTeam : match.ATeam;
}

export function getMatchResult(match: Match, teamId: number): MatchResult {
  let result = {
    title: "§",
    state: "unknown",
  };
  if (match.ATeamResult === match.BTeamResult) {
    result = {
      title: "U",
      state: "draw",
    };
  } else if (
    (`${match.ATeamId}` === `${teamId}` &&
      match.ATeamResult! > match.BTeamResult!) ||
    (`${match.BTeamId}` === `${teamId}` &&
      match.BTeamResult! > match.ATeamResult!)
  ) {
    result = {
      title: "S",
      state: "win",
    };
  } else if (
    (`${match.ATeamId}` === `${teamId}` &&
      match.ATeamResult! < match.BTeamResult!) ||
    (`${match.BTeamId}` === `${teamId}` &&
      match.BTeamResult! < match.ATeamResult!)
  ) {
    result = {
      title: "N",
      state: "loss",
    };
  }
  return result;
}

export function getOpponentMatchResults(
  matches: Match[],
  teamId: number
): MatchOutcome[] {
  return matches.map((match) => {
    const team = getOpponentTeamByIdFromMatch(match, teamId);

    return {
      team: team,
      matchId: match.Id!,
      result: getMatchResult(match, teamId),
    };
  });
}

export function getMatchResults(
  matches: Match[],
  teamId: number
): MatchOutcome[] {
  return matches.map((match) => {
    const team = getOpponentTeamByIdFromMatch(match, teamId);

    return {
      team: team,
      matchId: match.Id!,
      result: getMatchResult(match, teamId),
    };
  });
}

export function getGroupedGamesByKickoff(games: Match[]) {
  const groups = games.reduce((acc, game) => {
    const kickoffDate = new Date(game.Kickoff!).toISOString().split("T")[0];
    const group = (acc[kickoffDate] = acc[kickoffDate] || {
      date: game.Kickoff,
      games: [],
    });
    group.games.push(game);
    return acc;
  }, {} as Record<string, { date: string; games: Match[] }>);

  const sortedData = Object.keys(groups)
    .sort()
    .reduce((acc: Record<string, { date: string; games: Match[] }>, key) => {
      const group = groups[key];
      group.games.sort(
        (a, b) =>
          new Date(a.Kickoff!).getTime() - new Date(b.Kickoff!).getTime()
      );
      acc[key] = group;
      return acc;
    }, {});

  return sortedData;
}

function determineActiveState(game: Match): string {
  const gameState = getMatchState(game.MatchState);
  const startsAt = game.Kickoff ? new Date(game.Kickoff) : undefined;
  if (gameState === "post") {
    return "Post";
  } else if (gameState === "canceled") {
    return "Canceled";
  } else if (gameState === "rescheduled") {
    return "Rescheduled";
  } else if (startsAt instanceof Date) {
    // not scheduled yet, only has a date, not a kickoff time
    return "Rescheduled";
  } else {
    return "Pre";
  }
}

export function generateScoreBoard(game: Match, options: any = {}): Match {
  const courtRule = game["MatchState"] === "Judgement";
  const MatchState = getMatchState(game.MatchState);
  const equalJudgement =
    game["Judgement"] &&
    game["Judgement"]["ATeamValuationTeamAResult"] ===
      game["Judgement"]["BTeamValuationTeamBResult"] &&
    game["Judgement"]["ATeamValuationTeamBResult"] ===
      game["Judgement"]["BTeamValuationTeamAResult"];
  const halfScores =
    game["TeamResults"] &&
    game["TeamResults"].find((s: Result) => s["Rank"] === 1);

  const scoreBoard: MultiScoreboard = {
    sportstype: "soccer",
    MainScores: [],
    StartsAt: game.Kickoff,
  };

  let ATeamScore = undefined;
  let BTeamScore = undefined;

  if (!courtRule) {
    ATeamScore = game.ATeamResult;
    BTeamScore = game.BTeamResult;
  } else if (equalJudgement) {
    ATeamScore = game?.Judgement?.ATeamValuationTeamAResult;
    BTeamScore = game?.Judgement?.ATeamValuationTeamBResult;
  }

  const affiliations: Affiliation[] = [];

  if (options.include_league && game.Contest) {
    const affiliation: Affiliation = { title: game.Contest?.Name! };
    affiliations.push(affiliation);
  }

  if (options.include_round && game.Round) {
    const affiliation: Affiliation = { title: game.Round?.Name! };
    affiliations.push(affiliation);
  }

  if (affiliations.length > 0) {
    scoreBoard.affiliations = affiliations;
  }

  if (MatchState === "running") {
    const liveState: LiveState = {
      activeState: "Running",
      ts: Math.floor(new Date(game.Kickoff!).getTime() / 1000),
      text: "",
    };
    scoreBoard.State = liveState;
  } else {
    const timeState: TimeState = {
      activeState: determineActiveState(game),
      ts: Math.floor(new Date(game.Kickoff!).getTime() / 1000),
    };
    scoreBoard.State = timeState;
  }

  const score: Score = {
    score1: getResult(game.ATeamResult).toString(),
    score2: getResult(game.BTeamResult).toString(),
    active: MatchState === "running",
  };

  scoreBoard.MainScores.push(score);

  if (courtRule) {
    const gameStateScore: Score = {
      title: "§",
      score1: "",
      score2: "",
      active: false,
    };
    scoreBoard.GameStateScores = [gameStateScore];
  } else if (
    (MatchState === "running" || MatchState === "post" || options.live_score) &&
    halfScores?.ATeamResult != undefined &&
    halfScores?.BTeamResult != undefined
  ) {
    const gameStateScore: Score = {
      score1: getResult(halfScores?.ATeamResult).toString(),
      score2: getResult(halfScores?.BTeamResult).toString(),
      active: MatchState === "running",
    };
    scoreBoard.GameStateScores = [gameStateScore];
  }
  return {
    ...game,
    ScoreBoard: scoreBoard,
    ATeamScore,
    BTeamScore,
    ATeamHalfScore: halfScores?.ATeamResult,
    BTeamHalfScore: halfScores?.BTeamResult,
    CourtRule: courtRule,
    State: getMatchState(game?.MatchState) ?? undefined,
  };
}

function getMatchState(state?: string): MatchState {
  switch (state) {
    case "Judgement":
      return "post";
    case "Finished":
      return "post";
    case "Running":
      return "running";
    case "Canceled":
      return "canceled";
    case "Rescheduled":
      return "rescheduled";
    default:
      return "pre";
  }
}
